// ============================================================
// صدای فارسی مستقل از سیستم‌عامل
//
// مشکل: speechSynthesis مرورگر فقط صداهایی را دارد که روی دستگاه
// نصب‌اند. روی ویندوز معمولاً صدای فارسی نصب نیست، پس دستیار ساکت
// می‌ماند — و کاربر فکر می‌کند قابلیت خراب است.
//
// راه‌حل: مدل Piper فارسی که داخل خود مرورگر اجرا می‌شود
// (@diffusionstudio/vits-web، روی onnxruntime-web). نه کلید API
// می‌خواهد، نه سرور، و بعد از اولین بار آفلاین هم کار می‌کند.
//
// هزینه‌اش را صادقانه بگوییم: مدل ۶۳ مگابایت است و یک بار دانلود
// می‌شود. بعد در OPFS مرورگر می‌ماند و دفعات بعد آنی است. برای
// همین هیچ‌وقت بی‌خبر دانلود نمی‌شود — کاربر باید بداند.
//
// ایمپورت پویاست تا این حجم به باندل اصلی اضافه نشود؛ فقط وقتی
// بارگذاری می‌شود که واقعاً صدای فارسیِ سیستمی در کار نباشد.
// ============================================================

type VitsModule = typeof import("@diffusionstudio/vits-web");

/** صدای فارسی Amir؛ در نگاشت پیش‌فرض کتابخانه ثبت شده است. */
const VOICE_ID = "fa_IR-amir-medium";

export interface DownloadProgress {
    loaded: number;
    total: number;
    /** -1 یعنی حجم کل معلوم نیست (سرور Content-Length نداده) */
    percent: number;
    /** «۱۲.۴ از ۶۰.۶ مگابایت» — وقتی درصد نداریم هم چیزی برای نشان دادن هست */
    label: string;
}

/**
 * گزارش پیشرفت با هر تکه می‌آید — برای یک فایل ۶۳ مگابایتی یعنی
 * هزاران بار. اگر همه‌ی آن‌ها به state ری‌اکت برسند، پنل آن‌قدر
 * رندر می‌شود که عملاً قفل به نظر می‌رسد. پس کند می‌شود.
 */
const PROGRESS_THROTTLE_MS = 400;

/** مگابایت گرد شده به پایین — رقم اعشار در یک نوار پیشرفت فقط شلوغی است. */
const megabytes = (bytes: number): string =>
    String(Math.floor(bytes / (1024 * 1024)));

let modulePromise: Promise<VitsModule> | null = null;
let downloadPromise: Promise<void> | null = null;
let ready = false;
let unavailable = false;
let current: HTMLAudioElement | null = null;
let currentUrl = "";

// ---------- دانلود از سر گرفته‌شدنی ----------
//
// دانلودِ خود کتابخانه یک fetch ساده است: کل ۶۰ مگابایت را در حافظه
// جمع می‌کند و اگر اتصال وسط کار بیفتد، همه‌اش از بین می‌رود و از صفر
// شروع می‌شود. روی یک اینترنت ناپایدار، عملاً هیچ‌وقت تمام نمی‌شود.
//
// این پیاده‌سازی به‌جایش:
//   ۱) با Range از همان‌جا که مانده ادامه می‌دهد — حتی بعد از بستن صفحه
//   ۲) هر تکه را مستقیم روی دیسک می‌نویسد، نه در حافظه
//   ۳) چند بار با فاصله تلاش می‌کند
//
// نشانه‌ی «کامل شد» یک فایل جداگانه است، نه وجود خود فایل مدل: فایل
// نیمه‌کاره هم وجود دارد و اگر ملاک وجودش بود، مدلِ ناقص اجرا می‌شد.

const PIPER_DIR = "piper";
const MODEL_FILE = VOICE_ID + ".onnx";
const CONFIG_FILE = VOICE_ID + ".onnx.json";
const DONE_FILE = VOICE_ID + ".done";

/*
  مدل از *سرور خودمان* گرفته می‌شود، نه HuggingFace.

  دلیلش یک شکست واقعی بود: دانلود ۶۳ مگابایتی از HuggingFace (و
  ۱۸ مگابایت wasm از jsdelivr) روی اتصال ناپایدار وسط راه می‌افتاد و
  مرورگر فقط «NetworkError» می‌داد. فایل‌ها الان در public/piper/
  هستند و با `npm run piper:setup` یک بار گرفته می‌شوند.

  مسیر، تخت است: public/piper/fa_IR-amir-medium.onnx — نه ساختار
  پوشه‌ایِ HuggingFace.
*/
const LOCAL_BASE = "/piper";
const MODEL_PATH = MODEL_FILE;

/*
  نام فایلِ ذخیره‌شده در OPFS باید همان آخرین بخشِ آدرس باشد، چون
  خود کتابخانه موقع اجرا با همین نام سراغش می‌رود
  (url.split("/").at(-1)). MODEL_FILE و CONFIG_FILE همین شرط را
  دارند، پس مدلی که ما دانلود می‌کنیم را کتابخانه پیدا می‌کند و
  دیگر به شبکه نمی‌رود.
*/
// در بعضی مرورگرهای موبایل هدر Content-Range به‌خاطر CORS پنهان است.
// اندازه‌ی مدل Amir را مرجع می‌گیریم تا پاسخ ناقص 206 کامل حساب نشود.
const EXPECTED_MODEL_BYTES = 63531379;

const MAX_ATTEMPTS = 8;

const piperDir = async (): Promise<FileSystemDirectoryHandle> =>
    (await navigator.storage.getDirectory()).getDirectoryHandle(PIPER_DIR, { create: true });

const fileSize = async (dir: FileSystemDirectoryHandle, name: string): Promise<number> => {
    try {
        const handle = await dir.getFileHandle(name);
        return (await handle.getFile()).size;
    } catch {
        return 0;
    }
};

const hasFile = async (dir: FileSystemDirectoryHandle, name: string): Promise<boolean> => {
    try {
        await dir.getFileHandle(name);
        return true;
    } catch {
        return false;
    }
};

const writeWhole = async (
    dir: FileSystemDirectoryHandle,
    name: string,
    data: BlobPart
): Promise<void> => {
    const handle = await dir.getFileHandle(name, { create: true });
    const writable = await (handle as any).createWritable();
    await writable.write(data);
    await writable.close();
};

/** یک تلاش: از `from` تا هر جا که اتصال دوام بیاورد. تعداد بایت تازه را برمی‌گرداند. */
const downloadFrom = async (
    dir: FileSystemDirectoryHandle,
    from: number,
    onChunk: (loaded: number, total: number) => void
): Promise<{ written: number; total: number; complete: boolean }> => {
    const res = await fetch(LOCAL_BASE + "/" + MODEL_PATH, {
        headers: from > 0 ? { Range: "bytes=" + from + "-" } : {},
    });

    if (!res.ok) throw new Error("http " + res.status);

    // سرور ممکن است Range را نادیده بگیرد و از اول بفرستد
    const partial = res.status === 206;
    const start = partial ? from : 0;

    let total = 0;
    const range = res.headers.get("Content-Range");
    if (partial && range) {
        const match = range.match(/\/(\d+)\s*$/);
        if (match) total = Number(match[1]);
    }
    if (partial && !total) total = EXPECTED_MODEL_BYTES;
    if (!total) {
        const length = Number(res.headers.get("Content-Length") || 0);
        total = length ? (partial ? EXPECTED_MODEL_BYTES : length) : 0;
    }

    const handle = await dir.getFileHandle(MODEL_FILE, { create: true });
    const writable = await (handle as any).createWritable({ keepExistingData: start > 0 });

    let position = start;
    try {
        const reader = res.body?.getReader();
        if (!reader) throw new Error("no body");

        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            await writable.write({ type: "write", position, data: value });
            position += value.length;
            onChunk(position, total);
        }
    } finally {
        // بستن حتی در خطا: هر چه نوشته شده باید بماند تا تلاش بعدی
        // از همان‌جا ادامه بدهد.
        await writable.close();
    }

    return {
        written: position - start,
        total,
        complete: total > 0 && position >= total,
    };
};

const loadModule = (): Promise<VitsModule> => {
    if (!modulePromise) {
        modulePromise = import("@diffusionstudio/vits-web").catch((error) => {
            unavailable = true;
            throw error;
        });
    }
    return modulePromise;
};

/** آیا این مرورگر می‌تواند مدل را اجرا کند؟ */
export const isSupported = (): boolean => {
    if (unavailable) return false;
    if (typeof window === "undefined") return false;
    // مدل در OPFS نگه داشته می‌شود و در یک worker اجرا می‌شود
    return (
        typeof Worker !== "undefined" &&
        typeof WebAssembly !== "undefined" &&
        !!navigator.storage &&
        typeof navigator.storage.getDirectory === "function"
    );
};

/**
 * مدل کامل روی دستگاه هست؟ (بدون دانلود کردن)
 *
 * ملاک، فایل نشانه است نه خود مدل: فایل نیمه‌دانلودشده هم وجود
 * دارد و اگر وجودش ملاک بود، مدل ناقص اجرا می‌شد و فقط خطا می‌داد.
 */
export const isDownloaded = async (): Promise<boolean> => {
    if (ready) return true;
    if (!isSupported()) return false;

    try {
        const dir = await piperDir();
        const markedComplete = await hasFile(dir, DONE_FILE);
        const actualSize = await fileSize(dir, MODEL_FILE);
        ready = markedComplete && actualSize === EXPECTED_MODEL_BYTES;
        // اگر قبلاً یک پاسخ ناقص به‌اشتباه کامل علامت خورده باشد،
        // مهر را پاک می‌کنیم تا دانلود از همان‌جا ادامه پیدا کند.
        if (markedComplete && !ready) {
            try {
                await dir.removeEntry(DONE_FILE);
            } catch {
                /* تلاش دوباره‌ی دانلود همچنان ممکن است */
            }
        }
        return ready;
    } catch {
        return false;
    }
};

/** چند مگابایت از مدل تا الان گرفته شده — برای ادامه دادن بعد از قطعی. */
export const downloadedBytes = async (): Promise<number> => {
    if (!isSupported()) return 0;
    try {
        return await fileSize(await piperDir(), MODEL_FILE);
    } catch {
        return 0;
    }
};

export const isReady = (): boolean => ready;

let lastDownloadError = "";

/** آخرین خطای دانلود مدل برای نمایش علت واقعی شکست. */
export const downloadFailure = (): string => lastDownloadError;

/**
 * مدل را می‌گیرد. چند بار صدا زدنش یک دانلود بیشتر نمی‌سازد.
 *
 * `onProgress` برای این است که کاربر ۶۳ مگابایت را در سکوت منتظر
 * نماند.
 */
export const ensureDownloaded = async (
    onProgress?: (progress: DownloadProgress) => void
): Promise<boolean> => {
    if (ready) return true;
    if (!isSupported()) {
        lastDownloadError = "ذخیره‌سازی پایدار در این مرورگر در دسترس نیست";
        return false;
    }

    if (!downloadPromise) {
        downloadPromise = (async () => {
            const dir = await piperDir();

            let lastReport = 0;
            const report = (loaded: number, total: number) => {
                if (!onProgress) return;
                const now = Date.now();
                if (now - lastReport < PROGRESS_THROTTLE_MS) return;
                lastReport = now;

                onProgress({
                    loaded,
                    total,
                    percent: total ? Math.round((loaded / total) * 100) : -1,
                    label: total
                        ? megabytes(loaded) + " از " + megabytes(total) + " مگابایت"
                        : megabytes(loaded) + " مگابایت",
                });
            };

            // فایل پیکربندی کوچک است و یک تلاش بس است
            if (!(await hasFile(dir, CONFIG_FILE))) {
                const res = await fetch(LOCAL_BASE + "/" + MODEL_PATH + ".json");
                if (!res.ok) throw new Error("config http " + res.status);
                await writeWhole(dir, CONFIG_FILE, await res.blob());
            }

            // مدل: هر بار از همان‌جا که مانده ادامه می‌دهد
            let lastError: unknown = null;

            for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
                const from = await fileSize(dir, MODEL_FILE);

                if (from === EXPECTED_MODEL_BYTES) {
                    await writeWhole(dir, DONE_FILE, String(EXPECTED_MODEL_BYTES));
                    ready = true;
                    return;
                }

                try {
                    const outcome = await downloadFrom(dir, from, report);
                    if (outcome.complete) {
                        await writeWhole(dir, DONE_FILE, String(outcome.total));
                        ready = true;
                        return;
                    }
                    // اتصال بسته شد ولی چیزی گرفتیم — بلافاصله ادامه
                    if (outcome.written > 0) continue;
                } catch (error) {
                    lastError = error;
                }

                // هیچ بایتی نیامد: کمی صبر و دوباره
                await new Promise((resolve) => setTimeout(resolve, Math.min(8000, 1000 * attempt)));
            }

            throw lastError || new Error("download did not finish");
        })().catch((error) => {
            // اجازه می‌دهیم دفعه‌ی بعد دوباره تلاش شود
            downloadPromise = null;
            throw error;
        });
    }

    try {
        await downloadPromise;
        lastDownloadError = "";
        return true;
    } catch (error) {
        lastDownloadError = error instanceof Error ? error.message : String(error);
        return false;
    }
};

/**
 * مهار تعداد نخ‌های ONNX Runtime.
 *
 * این باگ باعث می‌شد مدل دانلود شود ولی هیچ‌وقت حرف نزند:
 *
 * ORT خودش محافظ دارد و وقتی صفحه cross-origin isolated نیست، روی
 * یک نخ می‌رود — ولی آن محافظ فقط وقتی اجرا می‌شود که numThreads
 * *تنظیم نشده* باشد. کتابخانه‌ی vits-web پیش از هر اجرا صریحاً
 * numThreads را روی تعداد هسته‌ها می‌گذارد، پس محافظ دور زده
 * می‌شود و ORT سراغ WASM چندنخی می‌رود که به SharedArrayBuffer
 * نیاز دارد. بدون هدرهای COOP/COEP آن شیء وجود ندارد و اجرا شکست
 * می‌خورد — بی‌صدا، چون ما به صدای مرورگر برمی‌گشتیم.
 *
 * اضافه کردن آن هدرها راه‌حل نیست: COEP بارگذاری منابع متقاطع را
 * می‌شکند و نقشه‌ها و تصاویر سامانه از کار می‌افتند.
 *
 * پس به‌جایش همان یک مقدار را قفل می‌کنیم. تک‌نخی کندتر است، ولی
 * کار می‌کند — و چند ثانیه تأخیر از سکوت بهتر است.
 */
let threadsPinned = false;

const pinThreads = async (): Promise<void> => {
    if (threadsPinned) return;
    threadsPinned = true;

    try {
        const ort: any = await import("onnxruntime-web");
        const isolated =
            typeof window !== "undefined" && (window as any).crossOriginIsolated;
        const allowed = isolated
            ? Math.max(1, Math.min(4, Math.ceil((navigator.hardwareConcurrency || 1) / 2)))
            : 1;

        let value = allowed;
        Object.defineProperty(ort.env.wasm, "numThreads", {
            configurable: true,
            get: () => value,
            // هر چه کتابخانه بگذارد، تا سقف مجاز پایین آورده می‌شود
            set: (next: number) => {
                value = Math.min(Number(next) || allowed, allowed);
            },
        });
    } catch {
        // اگر نشد، predict خودش خطا می‌دهد و پیامش به کاربر می‌رسد
    }
};

export const stop = (): void => {
    if (current) {
        try {
            current.pause();
        } catch {
            /* نادیده */
        }
        current = null;
    }
    if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
        currentUrl = "";
    }
};

/**
 * متن را می‌خواند. true یعنی پخش شروع شد.
 *
 * قرارداد مهم: در شکست، onDone صدا زده **نمی‌شود** و فقط false
 * برمی‌گردد — تا صداکننده بتواند همان متن را با صدای مرورگر بخواند
 * بی‌آنکه onDone دو بار اجرا شود. در موفقیت، onDone وقتی می‌آید که
 * پخش تمام (یا قطع) شده باشد؛ گفتگوی پیوسته به همین تکیه دارد تا
 * بداند کِی میکروفون را باز کند.
 */
export const speakWithPiper = async (
    text: string,
    onDone?: () => void
): Promise<boolean> => {
    if (!ready || !isSupported()) return false;

    stop();

    try {
        await pinThreads();
        const vits: any = await loadModule();
        // کتابخانه فقط دو صدای فارسی قدیمی را در PATH_MAP دارد. مدل‌های
        // جدیدتر را مستقیم دانلود می‌کنیم، پس ثبت مسیرش لازم است تا
        // predict همان فایل‌های ذخیره‌شده در OPFS را برای تولید صدا بردارد.
        if (!vits.PATH_MAP) throw new Error("voice path map unavailable");
        vits.PATH_MAP[VOICE_ID] = MODEL_PATH;
        const wav = await vits.predict({ text, voiceId: VOICE_ID as any });

        const url = URL.createObjectURL(wav);
        const audio = new Audio(url);

        current = audio;
        currentUrl = url;

        let finished = false;
        const finish = () => {
            if (finished) return;
            finished = true;
            if (currentUrl === url) {
                URL.revokeObjectURL(url);
                currentUrl = "";
            }
            if (current === audio) current = null;
            if (onDone) onDone();
        };

        audio.onended = finish;
        audio.onerror = finish;
        audio.onpause = finish;

        await audio.play();
        return true;
    } catch (error) {
        // بدون این، شکست اجرای مدل فقط به سکوت ترجمه می‌شد و هیچ
        // سرنخی برای فهمیدن علت نمی‌ماند.
        lastError = error instanceof Error ? error.message : String(error);
        // eslint-disable-next-line no-console
        console.error("[صدای فارسی] اجرای مدل ناموفق بود:", error);
        stop();
        return false;
    }
};

let lastError = "";

/** آخرین علت شکست خواندن — برای نشان دادن به کاربر، نه فقط کنسول. */
export const lastFailure = (): string => lastError;

/**
 * یک جمله را با مدل می‌خواند و می‌گوید موفق بود یا نه.
 *
 * برای آزمودن درست بعد از دانلود: بهتر است همان لحظه بفهمیم اجرا
 * کار نمی‌کند تا اینکه کاربر بعداً با سکوت روبه‌رو شود.
 */
export const trySpeak = (text: string): Promise<boolean> => speakWithPiper(text);
